import {Page} from "../page"

export const groups: Page = {
    uri: 'learn/groups',
    icon: '👥',
    title: $localize`:@@page-groups-title:Groups`,
    seoTitle: $localize`:@@page-groups-seo-title:Board game groups — shared shelves, feeds and club life`,
    summary: $localize`:@@page-groups-summary:Community spaces for gaming crews, clubs, and friends to organize events and share collections.`,
    markdownContent: [
        $localize`:@@page-groups-creating-a-group:
Groups are community spaces where gaming crews, clubs, societies, and friends can connect, organize events, and share their game collections. Every group has its own feed, member roster, and shared game data—making it easy to plan game nights and discover what everyone owns.

![Group page with members, feed, and upcoming events](/assets/screenshots/group-page.webp)

## Creating a Group

To start one, tap the **+** button and choose **New community**. The dialog that opens offers three routes: create a **standalone community** you run from your own account, **convert this account** into an organization, or **log out and create a dedicated account** for it. The standalone option is the easy default — it has no login of its own, and you can promote it to a full [organization](/learn/organizations) later without losing anything.

![The New community dialog with its three creation routes](/assets/screenshots/groups-new-community-dialog.webp)

You'll set up:

- **Name**: What you're calling your group (e.g., "Board Game Nerds of Brooklyn")
- **Description**: A brief overview of what the group is about
- **Visibility**: Choose who can find and take part
  - **Public**: Anyone can view *and* participate — post, host events, log plays
  - **Semi-Private**: Anyone can find and browse the group, but only members can participate
  - **Private**: Hidden from non-members; only members can view or participate
- **Join Policy**: How people join
  - **Open**: Anyone can join immediately
  - **Request**: People apply; you approve or decline
  - **Invite only**: You send invites to specific people

Once created, you're the first **Admin** of the group with full control. You can promote other members to admin at any time — a group can have as many admins as it needs, and they all share the same level of authority.`,

        $localize`:@@page-groups-roles-permissions:## Roles & Permissions

Each member has a role that determines what they can do. Admins can customize permissions for each action:

- **Admin**: Full control. Can edit group settings, manage members, promote or demote roles, configure permissions, and delete the group. A group can have multiple admins, all with equal authority — there's no single "owner" above them.
- **Organizer**: Can host events, log plays, and moderate content. Good for event coordinators and active contributors.
- **Member**: Basic permissions — usually can post and log plays. What members can actually do depends on the per-role permissions the admins have configured.

![The Manage member dialog with role selection and overrides](/assets/screenshots/groups-manage-member.webp)

### Customizable Permissions

Rather than fixing what each role may do, admins set a **minimum audience per action**. Each of these can be Anyone, Members, a [membership tier](/learn/membership-tiers) and up, Organizers, or Admins:

- **Who can post**
- **Who can host events**
- **Who can host scheduled games**
- **Who can log plays**
- **Who can invite**
- **Who can see members**

![Per-action permission dropdowns for a community](/assets/screenshots/onboarding-org-setup.webp)

### Per-Member Overrides

Sometimes one person needs an exception. Open any member from the member list and you can override the group defaults just for them — **allow** or **deny** hosting events and scheduling games, regardless of their role. There's also an **always tentative** switch for members whose attendance is habitually uncertain, so their RSVPs register as provisional. Every event's console lists them in its **Always a maybe** card, and to keep someone as a maybe at one event only, mark them there instead (see [running an event](/learn/event-operations)).`,

        $localize`:@@page-groups-managing-members-v2:## Managing Members

### Joining & Approval

If your group uses **Request** or **Invite only**, you'll see pending requests in the club console's **Members** section. Approve or decline each one. For **Invite only** groups, share an invite link or QR code, or search for users by name to invite them directly.

![The member list with roles and per-member manage buttons](/assets/screenshots/groups-members-list.webp)

### Changing Roles

Tap a member to open **Manage member**, then pick their **Role**: **Member** (can view and take part), **Organizer** (can also help run the group) or **Administrator** (full control, including members and settings). Any admin can change any other member's role, including promoting someone to Admin or demoting a fellow admin.

The per-member exceptions described above live in the same dialog, under **Overrides**.

### Removing Members

Tap a member and choose **Kick** in the **Manage member** dialog. Ludoya asks you to confirm first, and once you do they lose access to the group immediately.

### Invite Links & QR Codes

Generate a shareable link or QR code to invite friends — great for posters, flyers, or the counter at your local shop.

![The invite dialog with user invite, link, and QR code options](/assets/screenshots/groups-invite-dialog.webp)

Worth knowing: anyone who joins through the link or QR code is **accepted automatically**, even when your group normally makes people request to join. The link *is* the invitation, so only share it where you're happy for anyone to walk in.`,

        $localize`:@@page-groups-group-features:## Group Features

### Official Events

Host events directly under your group. Everyone in the group sees them in the group's event feed. Events can be tied to a venue or location, and members can RSVP to plan attendance.

See [Events](/learn/events) for details on scheduling and managing events.

### Group Collection

View all games owned by group members in one place—perfect for planning what to play next or discovering hidden gems you didn't know someone had.

![A group's collective collection sorted by total play time](/assets/screenshots/groups-collective-collection.webp)

Open it with **Collective Games**, under the game library on the group's profile. It shows:

- Every game someone in the group owns
- Who owns it: a face and an owner count on each game, which you select for their names and profiles (so you can borrow or coordinate plays)
- Owned vs. played counts

Use this to answer questions like *"Does anyone have Catan?"* or *"What new games could we play?"*

### Game Summaries

See aggregate play data within the group—most-played games, average scores, play frequency. Great for understanding the group's gaming preferences.

### Pinned Posts

Admins can pin one post to the top of the group profile — an announcement, the house rules, or the next big event. Pinning a different post replaces it, so the pin always points at the one thing that matters most right now.

### Group Feed

A focused activity stream showing only posts, plays, and events from your group. Separate from the global and regional feeds, so conversations stay organized.

### Club Chat

Admins can give the club one [chat](/learn/messaging#page-messaging-linked-chats) with every member in it, from **Who can write in the club chat** in the console's settings. People join it when they join the club and leave it when they leave, and members open it from the **Club chat** row on the club's page or from their messages. The admins choose whether every member writes in it or only organizers or admins, for an announcements channel everyone can still react to.`,

        $localize`:@@page-groups-discovering-groups:## Discovering Groups

Head to **Explore → Communities** and search by name, or just browse what's around you. Sort the results by:

- **Relevance** — the default blend of closeness and activity
- **Distance** — nearest first
- **Members** — biggest communities first
- **Alphabetical** — plain A to Z

You can also filter by group type (club, shop, publisher, festival), by whether you're a member, by minimum size, and by what's **open now** — or switch to the **map** and search a specific neighborhood.

![Explore Communities with search, type filters, and map view](/assets/screenshots/explore-communities.webp)

### A Club's Page

A club's page starts with its description, where it meets and when it's open, how many follow it and how many are members, and the buttons to **Follow** it (what it shares then reaches your Following feed, without joining) and to **Join**, **Request to Join** or **Accept Invitation**. A club that charges for membership says what it costs right there, with **See memberships** for its tiers; one that takes bookings has **Book a visit**.

Below come the parts the club has: the **Club chat** for members, its **Events**, its **Game library** (with **Collective Games**, what the members own between them), its plays and **Stats**, the **Club market** for members, and its **Activity**, the posts from the club and its members. Members also find **What members want to play** in the page's ⋮ menu.

Signed in, **Show me around** at the bottom of the page's ⋮ menu walks you through it, one control at a time; the page offers it the first few times you open a club.`,

        $localize`:@@page-groups-embeddable-widgets:## Embeddable Widgets

As an Admin, you can generate HTML snippets to embed on your group's external website (see [Integrations](/learn/integrations)):

- **Events widget**: Shows upcoming events
- **Games widget**: Displays group's collection
- **Plays widget**: Recent play activity

![An embedded collection widget on an external website](/assets/screenshots/embed-collection.webp)

Share your group's activity beyond Ludoya—great for club websites.`,

        $localize`:@@page-groups-promoting-to-an-organization:## Promoting to an Organization

A group is already an account — it has a username, a profile, and a collection of its own. Give it an email and password whenever you want and it becomes a full [organization](/learn/organizations): staff can sign in as it directly. A [premium plan](/premium) for tournaments, ticketing or floor plans doesn't need that: any admin can buy one for the group as it is. Nothing is lost in the change, and you can leave it as long as you like.

See [Organizations](/learn/organizations) to learn about the conversion process and what you gain.`,

        $localize`:@@page-groups-section-8:**See also:**
- [Organizations](/learn/organizations) — dedicated accounts for clubs and stores
- [Events](/learn/events) — hosting and joining game sessions
- [Social & Feeds](/learn/social-feeds) — activity streams and discovery
- [Collection](/learn/collection) — tracking games you own
- [Statistics](/learn/statistics) — play data and trends`,
    ].join('\n\n---\n\n')
}
